import { queryOptions, useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { z } from 'zod';

import { ONE_HOUR } from '@/constants/time';
import { cancerFocusSchema } from '@/services/getCancerTypesV2';
import { userIdAtom } from '@/services/globalState';
import { paths } from '@primum/api-types';
import { fetchClient } from '@/util/fetchClient';

export const inquiryCancerTypesSchema = z.array(cancerFocusSchema);

export type InquiryCancerType = z.infer<typeof cancerFocusSchema>;

export type InquiryCancerTypesResponse =
  paths['/v2/inquiries/cancer-types']['get']['responses'][200]['content']['application/json']['data'];

export type InquiryCancerTypeData = InquiryCancerTypesResponse[0];

const CANCER_TYPES_QUERY_KEY = ['inquiryCancerTypes'] as const;

async function getInquiryCancerTypes() {
  const { data } = await fetchClient.GET('/v2/inquiries/cancer-types');

  if (!data?.data) {
    return data?.data;
  }

  const seenShortLabels = new Set<string>();

  return data.data.filter((cancerType) => {
    const shortLabel = cancerType.shortLabel ?? cancerType.label ?? '';

    if (seenShortLabels.has(shortLabel)) {
      return false;
    }

    seenShortLabels.add(shortLabel);

    return true;
  });
}

export const getInquiryCancerTypesQueryOptions = (userId: string | undefined) =>
  queryOptions({
    queryKey: [...CANCER_TYPES_QUERY_KEY, userId] as const,
    queryFn: getInquiryCancerTypes,
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export function useInquiryCancerTypesQuery() {
  const userId = useAtomValue(userIdAtom);

  return useQuery(getInquiryCancerTypesQueryOptions(userId));
}
